import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { isNativeApp } from "@/lib/native/platform";
import { rememberVendor } from "@/lib/native/vendorMemory";
import { bindPushTokenToVendor } from "@/lib/native/push";
import { saveOrderGuideSnapshot } from "@/lib/native/offlineCache";
import {
  biometricAvailability,
  markBiometricOffered,
  setBiometricEnabled,
  wasBiometricOffered,
} from "@/lib/native/biometric";

// All Capacitor-shell side effects for the customer ordering page, kept in one
// self-contained component so customer-entry.tsx carries a single extra line.
// Renders nothing on the web; on native it:
//   - remembers this vendor for the launch screen's "Your vendors" list
//   - binds the device push token to this vendor's session (reminder pushes)
//   - snapshots the order guide for the offline screen
//   - once ever, offers to enable Face ID / fingerprint unlock

interface Props {
  orgSlug?: string;
  /** The signed-in account — part of the push-binding memo (sub-account switches re-bind). */
  accountId?: number;
  orgName?: string;
  logoUrl?: string | null;
  /** Org favicon — the square mark the launch-screen vendor tiles render. */
  faviconUrl?: string | null;
  customUrl?: string | null;
  sessionVerified: boolean;
  /** Demo orgs are never remembered as vendors and never get push/biometric offers. */
  isDemo?: boolean;
  products: Array<{ name: string; unit?: string | null }>;
}

export function NativeShellIntegration({ orgSlug, accountId, orgName, logoUrl, faviconUrl, customUrl, sessionVerified, isDemo, products }: Props) {
  const [biometricOffer, setBiometricOffer] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const boundRef = useRef(false);

  // Warm-offline banner: the SPA is already loaded when connectivity drops
  // (the Airplane-Mode reviewer pass). Cached React Query data keeps the menu
  // readable; this banner names the state instead of letting requests fail
  // silently. navigator.onLine is enough — no plugin needed.
  useEffect(() => {
    if (!isNativeApp()) return;
    const sync = () => setOffline(!navigator.onLine);
    sync();
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
    };
  }, []);

  // Vendor memory: once per page load, when the session is live.
  // Skipped entirely for demo orgs: the demo is a try-it surface, not a supplier —
  // remembering it pollutes the launch screen's vendor list, and binding push or
  // offering biometrics on an ephemeral demo session makes no sense.
  useEffect(() => {
    if (!isNativeApp() || !sessionVerified || !orgSlug || !orgName || isDemo) return;
    if (boundRef.current) return;
    boundRef.current = true;

    rememberVendor({ slug: orgSlug, orgName, iconUrl: faviconUrl, logoUrl, customUrl });

    // One-time biometric enrollment offer.
    (async () => {
      if (await wasBiometricOffered()) return;
      const { available, label } = await biometricAvailability();
      if (!available) return;
      setBiometricOffer(label);
    })();
  }, [sessionVerified, orgSlug, orgName, logoUrl, faviconUrl, customUrl, isDemo]);

  // Push binding waits for the account, which loads after the session check.
  // Same demo exclusion as above. bindPushTokenToVendor is memoized per
  // (vendor, account, token), so re-running this is cheap.
  useEffect(() => {
    if (!isNativeApp() || !sessionVerified || !orgSlug || !accountId || isDemo) return;
    void bindPushTokenToVendor(orgSlug, accountId);
  }, [sessionVerified, orgSlug, accountId, isDemo]);

  // Offline snapshot: refresh whenever the product list changes while online.
  useEffect(() => {
    if (!isNativeApp() || !orgName || products.length === 0) return;
    saveOrderGuideSnapshot(orgName, products);
  }, [orgName, products]);

  return (
    <>
      {offline && (
        <div
          className="fixed top-0 inset-x-0 z-[100] bg-amber-500 text-white text-center text-sm font-medium py-2 px-4"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
          data-testid="native-offline-banner"
        >
          You&rsquo;re offline — viewing your saved order guide. Reconnect to place an order.
        </div>
      )}
      {biometricOffer && (
    <AlertDialog open onOpenChange={(open) => { if (!open) setBiometricOffer(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unlock with {biometricOffer}?</AlertDialogTitle>
          <AlertDialogDescription>
            Use {biometricOffer} to open your vendors from the FreshOrder home
            screen without re-entering your PIN.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={async () => {
              await markBiometricOffered();
              setBiometricOffer(null);
            }}
          >
            Not now
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={async () => {
              await setBiometricEnabled(true);
              await markBiometricOffered();
              setBiometricOffer(null);
            }}
          >
            Enable {biometricOffer}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
      )}
    </>
  );
}
